import React, { useState, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import FamilyChangeDialog from "./FamilyChangeDialog";
import { getFamilyCategory } from "./familyCategoryUtils";

export const applySequentially = async (items, fn) => {
  for (const item of items) {
    await fn(item);
  }
};

const DEFAULT_MESSAGE = "סמן את המוצרים שתרצה לשנות בנוסף אליו:";

const productIdOf = (item) =>
  String(item?.productId ?? item?.product_id ?? item?.id ?? "");

export const useFamilyProductUpdate = ({ message = DEFAULT_MESSAGE } = {}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [onCancelCallback, setOnCancelCallback] = useState(null);
  const lastFamilySelectionRef = useRef(null);

  const product = useSelector((state) => state.selectedProduct.product);
  const categories = useSelector(
    (state) => state.selectedProduct.productCategories,
  );

  const familyCategory = getFamilyCategory(categories);

  const markedProducts = useMemo(() => {
    if (!product) return [];
    const saved = lastFamilySelectionRef.current;
    const familySlug = familyCategory?.slug;
    if (saved?.slug && saved.slug === familySlug && saved.ids?.length) {
      const currentId = productIdOf(product);
      return saved.ids.map((id) =>
        id === currentId ? product : { productId: id },
      );
    }
    return [product];
  }, [product, familyCategory?.slug]);

  const requestUpdate = useCallback(
    (updateFn, onCancel) => {
      if (!product) return;

      if (familyCategory) {
        setPendingUpdate(() => updateFn);
        setOnCancelCallback(() => onCancel ?? null);
        setShowDialog(true);
        return;
      }

      updateFn([product]);
    },
    [familyCategory, product],
  );

  const dismissDialog = useCallback(() => {
    setShowDialog(false);
    setPendingUpdate(null);
    setOnCancelCallback(null);
  }, []);

  const handleDismiss = useCallback(() => {
    onCancelCallback?.();
    dismissDialog();
  }, [onCancelCallback, dismissDialog]);

  const handleConfirm = useCallback(
    async (productsToChange) => {
      if (!pendingUpdate) return;
      if (familyCategory?.slug) {
        lastFamilySelectionRef.current = {
          slug: familyCategory.slug,
          ids: productsToChange.map(productIdOf).filter(Boolean),
        };
      }
      const succeeded = await pendingUpdate(productsToChange);
      if (succeeded !== false) {
        dismissDialog();
      }
    },
    [pendingUpdate, dismissDialog, familyCategory?.slug],
  );

  const familyDialog =
    showDialog && familyCategory && product ? (
      <FamilyChangeDialog
        product={product}
        close={handleDismiss}
        onConfirm={handleConfirm}
        markedProducts={markedProducts}
        massage={message}
        slug={familyCategory.slug}
      />
    ) : null;

  return { requestUpdate, familyDialog, isInFamily: !!familyCategory };
};

export default useFamilyProductUpdate;
